import {
  IconAnalytics,
  IconAtom,
  IconBell,
  IconBellGray,
  IconBinocularsGray,
  IconChainLink,
  IconChainLinkGray,
  IconChartBar,
  IconChartBarGray,
  IconChartRadius,
  IconChartRadiusGray,
  IconClockGray,
  IconCloudCheckGray,
  IconCoil,
  IconDashboard,
  IconLayers,
  IconLocation,
  IconPenSquare,
  IconRoute,
  IconRulerGray,
  IconSearch,
  IconSecurity,
  IconTagGray,
  IconTvocGray,
  IconUserCheck,
  IconWarning,
  IconWarningGray,
  IconZoneGray,
} from '@assets/images/icon';
import { IconTemp } from '@assets/images/main';
import { rtlsSystem } from '@assets/images/solution';
import video from '@src/assets/images/solution/manufacturingProcess/video/rtlsVideo.mp4';

import image1 from '@src/assets/images/solution/manufacturingSteel/img/1.avif';
import image2 from '@src/assets/images/solution/manufacturingSteel/img/2.avif';
import image3 from '@src/assets/images/solution/manufacturingSteel/img/3.avif';
import image4 from '@src/assets/images/solution/manufacturingSteel/img/4.avif';
import image5 from '@src/assets/images/solution/manufacturingSteel/img/5.avif';
import image6 from '@src/assets/images/solution/manufacturingSteel/img/6.avif';
import image7 from '@src/assets/images/solution/manufacturingSteel/img/7.avif';
import image8 from '@src/assets/images/solution/manufacturingSteel/img/8.avif';

export default {
  type: 'Manufacturing Solution',
  banner: {
    title: 'Manage inventory assets in large logistics warehouses efficiently and accurately with AI.',
    desc: 'By leveraging AI video recognition, automatically identify various logistics assets such as steel coils to manage inventory accurately and efficiently.',
  },

  video: {
    title: 'Manage inventory assets in real time with AI even in complex logistics environments.',
    desc: 'Traditional manual counting is inefficient and error-prone in complex logistics settings. ORBRO’s AI system automatically detects and manages various logistics assets like steel coils, boosting warehouse operation efficiency.',
    // play: video,
    image: image1,
  },

  func: [
    {
      theme: 'Precisely manage various logistics assets with AI video analysis.',
      title: 'AI video-based automatic asset counting',
      desc: 'AI cameras accurately recognize and automatically count various logistics assets like steel coils in real time. Enables fast and precise inventory control even in complex warehouses.',
      sub1: 'Real-time status monitoring',
      icon1: IconClockGray,
      sub2: 'Provide statistical data',
      icon2: IconChartBarGray,
      image: image2,
    },
    {
      title: 'AI recognition and management of steel coils and pallets',
      desc: 'Accurately recognize both steel coils and transport pallets simultaneously using AI video technology, enabling management of complex assets. Efficiently handle mixed assets with different shapes.',
      sub1: 'Real-time location tracking',
      icon1: IconBinocularsGray,
      sub2: 'High accuracy',
      icon2: IconRulerGray,
      image: image3,
    },
    {
      title: 'Learning and automatic management of various asset models',
      desc: 'AI learns and recognizes various asset types including steel coils and pallets, ensuring precise management of complex assets.',
      sub1: 'Specify risky assets',
      icon1: IconWarningGray,
      sub2: 'Set response actions',
      icon2: IconChartRadiusGray,
      image: image4,
    },
    {
      theme: 'Maximize logistics operation efficiency through automated data analysis.',
      title: 'Real-time inventory status by zone',
      desc: 'Instantly check the inventory status of each zone in the warehouse, enabling accurate, data-driven operations and quick decision-making.',
      sub1: 'Store data in the cloud',
      icon1: IconClockGray,
      sub2: 'Provide statistical data',
      icon2: IconChartBarGray,
      image: image5,
    },
    {
      title: 'Track and forecast inventory volume changes by time',
      desc: 'Automatically record and analyze inventory volume changes by time slot to accurately forecast logistics flow and respond effectively.',
      sub1: 'Specify risky assets',
      icon1: IconWarningGray,
      sub2: 'Set response actions',
      icon2: IconChartRadiusGray,
      image: image6,
    },
    {
      title: 'Automate manual inventory management',
      desc: 'Use AI-based automatic counting to eliminate errors and time loss caused by manual inventory management.',
      sub1: 'Store data in the cloud',
      icon1: IconClockGray,
      sub2: 'Provide statistical data',
      icon2: IconChartBarGray,
      image: image7,
    },
    {
      title: 'Export analysis data as CSV',
      desc: 'Easily export analyzed inventory and logistics data as CSV files for various reports and deeper analysis.',
      sub1: 'Store data in the cloud',
      icon1: IconClockGray,
      sub2: 'Provide statistical data',
      icon2: IconChartBarGray,
      image: image8,
    },
  ],

  system: {
    title: 'System Structure',
    img: rtlsSystem,
  },

  productTitle: 'Product Packages',
  product: [
    {
      tag: 'assetPackage',
    },
    {
      tag: 'workerPackage',
    },
  ],

  subfunc: {
    title: 'Key Features Overview',
    desc: '',

    data: [
      {
        icon: IconAtom,
        title: 'AI-based automatic asset recognition',
        desc: 'Automatically recognize assets in real time with AI technology for precise and efficient management.',
      },
      {
        icon: IconCoil,
        title: 'Steel coil and pallet recognition',
        desc: 'Simultaneously recognize steel coils and pallets with AI to manage inventory accurately even in complex conditions.',
      },
      {
        icon: IconLocation,
        title: 'Asset location and status monitoring',
        desc: 'Analyze asset location and condition in the warehouse in real time for efficient management.',
      },
      {
        icon: IconPenSquare,
        title: 'Automate manual inventory management',
        desc: 'Automate manual inventory tasks to greatly improve work efficiency and accuracy.',
      },
      {
        icon: IconDashboard,
        title: 'Real-time inventory dashboard',
        desc: 'Provides a real-time dashboard for a clear overview of warehouse asset status.',
      },
      {
        icon: IconRoute,
        title: 'Asset movement path analysis',
        desc: 'Analyze asset movement routes and usage patterns with AI to support effective operation planning.',
      },
      {
        icon: IconLayers,
        title: 'Real-time zone-based inventory management',
        desc: 'Check asset status in each zone in real time for accurate and efficient management.',
      },
      {
        icon: IconChartBar,
        title: 'Analyze inventory fluctuations by time',
        desc: 'Analyze inventory changes by time slot to predict logistics flow and respond quickly.',
      },
      {
        icon: IconSecurity,
        title: 'High AI recognition accuracy',
        desc: 'AI recognizes various assets with over 99% accuracy, providing highly reliable data.',
      },
      {
        icon: IconAnalytics,
        title: 'Support decision-making based on operational data',
        desc: 'Support fast and accurate decisions and strategy planning based on analyzed operational data.',
      },
    ],
  },

  contents: [
    {
      data: 'manufacturingSteel',
    },
    {
      data: 'logisticsAsset',
    },
    {
      data: 'blogMedical',
    },
    {
      data: 'blogRTLS',
    },
  ],
};
